/**
 * Client for the backend REST proxy.
 *
 * POST /api/proxy/execute accepts a REST action definition and executes it
 * server-side, returning a structured response envelope regardless of the
 * upstream HTTP status.
 */

import type { RestAction } from '../types/project';
import { apiFetch } from './apiClient';

// ── Response types ────────────────────────────────────────────────────────────

export type ProxyResponse = {
  ok: boolean;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: unknown;
  durationMs: number;
};

// ── API call ──────────────────────────────────────────────────────────────────

/**
 * Sends a REST action definition to the backend proxy for execution.
 *
 * Returns a ProxyResponse on success (including upstream non-2xx).
 * Throws an Error with a human-readable message on network failure or a
 * non-200 response from the proxy itself (e.g. 400 validation error).
 */
export async function executeAction(action: RestAction, project?: {id:string;name:string}): Promise<ProxyResponse> {
  const res = await apiFetch('/api/proxy/execute', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({...action,projectId:project?.id,projectName:project?.name}),
  });

  if (!res.ok) {
    // The proxy itself rejected the request (400/502) — extract the error message
    let message = `Proxy error: HTTP ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore parse failure
    }
    throw new Error(message);
  }

  return res.json() as Promise<ProxyResponse>;
}
